import { useState } from "react";
import { istFreigabeKandidat } from "./pluginGate";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Plugin } from "@/types";
import { useBulkApprovePlugins } from "@/api/mutations/plugins";

// #288 (UC-6): Mehrere Plugins auf einmal freischalten — der Regelfall, wenn ein Katalog
// befüllt wurde. Serverseitig bekommt trotzdem JEDER Eintrag seine eigene Entscheidung und
// seinen eigenen Audit-Datensatz; hier wird nur der Klick gebündelt, nicht die Entscheidung.
//
// Die Leiste erscheint nur, wenn es in EINER Quelle etwas freizuschalten gibt — über mehrere
// Quellen hinweg wäre „alle freigeben" eine Sammelaussage, die niemand geprüft hat.
export function BulkApproveBar({ plugins }: { plugins: Plugin[] }) {
  const bulk = useBulkApprovePlugins();
  const [laeuft, setLaeuft] = useState(false);

  const offen = plugins.filter(istFreigabeKandidat);
  if (offen.length === 0) return null;

  const quellen = new Set(offen.map((p) => p.marketplaceId));
  const einzelneQuelle = quellen.size === 1 ? offen[0].marketplaceId : null;

  return (
    <div
      className="mb-3 flex items-center gap-3 rounded-md border border-warn-border bg-warn-bg px-3 py-2 text-[12.5px]"
      data-testid="bulk-approve-bar"
    >
      <ShieldCheck className="h-4 w-4 shrink-0 text-warn" />
      <div className="flex-1">
        <span className="font-semibold">
          {offen.length === 1 ? "1 Plugin ist nicht freigeschaltet" : `${offen.length} Plugins sind nicht freigeschaltet`}
        </span>
        <span className="opacity-80">
          {" "}
          — sie lassen sich keinem Agent zuweisen, bis jemand sie freigibt.
        </span>
      </div>
      {einzelneQuelle ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={laeuft || bulk.isPending}
          onClick={() => {
            setLaeuft(true);
            bulk.mutate(
              { marketplaceId: einzelneQuelle, pluginNames: offen.map((p) => p.name), approved: true },
              {
                onSuccess: (r) => {
                  if (r.failed.length === 0) toast.success(`${r.done.length} Plugins freigeschaltet`);
                  // Teil-Erfolge werden BENANNT statt als Erfolg verbucht — sonst hielte der
                  // Admin etwas für freigeschaltet, das es nicht ist.
                  else
                    toast.warning(
                      `${r.done.length} freigeschaltet, ${r.failed.length} nicht: ${r.failed.map((f) => f.name).join(", ")}`,
                    );
                },
                onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
                onSettled: () => setLaeuft(false),
              },
            );
          }}
          data-testid="bulk-approve-button"
        >
          Alle {offen.length} freischalten
        </Button>
      ) : (
        <span className="opacity-70">Je Quelle freischalten (Marketplace öffnen)</span>
      )}
    </div>
  );
}
